import { useEffect, useRef, useState } from 'react';

import * as vision from '@mediapipe/tasks-vision';

import { REACT_APP_CDN_URL } from 'envs';

const { FaceLandmarker, FilesetResolver } = vision;

/* The same WASM and model the applicant flow loads (kyc.frontend
   `views/facial-enroll/hooks/use-face.ts`), from the same asset CDN. */
const WASM_URL = `${REACT_APP_CDN_URL}/%40mediapipe/tasks-vision%400.10.3/wasm`;
const MODEL_URL = `${REACT_APP_CDN_URL}/%40mediapipe/face_landmarker.task`;

/**
 * THE APPLICANT FLOW'S OWN HEURISTIC, thresholds included. It reads two eye
 * blendshapes rather than a head-pose angle: with the eyes held on the camera,
 * turning the head right drives `eyeLookInRight` up, and turning it left drives
 * `eyeLookInLeft` up. The right turn is required FIRST, and the left only
 * counts after it — the order is what makes it a gesture rather than a glance
 * (kyc.frontend `facial-enroll.tsx`, `moveRightRef`/`moveLeftRef`).
 */
const RIGHT_THRESHOLD = 0.65;
const LEFT_THRESHOLD = 0.45;
/** Both scores under this, with a face in frame, reads as looking straight. */
const STRAIGHT_MAX = 0.35;
/** Inference cadence. Every frame is wasted work for a gesture this slow. */
const FRAME_INTERVAL_MS = 100;

export type IHeadTurnPhase = 'right' | 'left' | 'straight' | 'done';

export interface IHeadTurn {
	/**
	 * `bypassed` is the applicant flow's own answer to a landmarker that cannot
	 * load: it sets both gesture flags and lets the capture proceed. Blocking a
	 * transfer because a CDN was unreachable would be worse than capturing
	 * without the gesture — but the panel SAYS so when it happens.
	 */
	status: 'idle' | 'loading' | 'ready' | 'bypassed';
	phase: IHeadTurnPhase;
	faceVisible: boolean;
	rightDone: boolean;
	leftDone: boolean;
	/** The gesture is complete (or bypassed): Capture may proceed. */
	passed: boolean;
}

const INITIAL: IHeadTurn = {
	status: 'idle',
	phase: 'right',
	faceVisible: false,
	rightDone: false,
	leftDone: false,
	passed: false,
};

const scoreOf = (
	categories: Array<{ categoryName?: string; displayName?: string; score: number }>,
	name: string
): number =>
	categories.find(c => (c.displayName || c.categoryName) === name)?.score ?? 0;

/**
 * Drive the head-turn gesture over a live `<video>`.
 *
 * A CAPTURE GATE, NOT A LIVENESS VERDICT. What this establishes is that the
 * camera saw a face turn right, then left, then come back to centre before the
 * burst was taken — the same check the applicant flow runs before its capture.
 * It is decided in the browser and recorded nowhere: `POST /face-verifications`
 * takes only the frames, and the transfer's proof of identity remains the
 * enrolment biometrics.api makes from them. Nothing here is, or is described
 * as, a liveness result.
 *
 * Inference runs only while `active` and stops the moment it is not: the
 * landmarker is closed and the frame loop cancelled on every deactivation, so
 * leaving the capture screen (Retake, Back, closing the modal) never leaves a
 * model running against a video that is gone.
 */
export const useHeadTurn = (
	video: HTMLVideoElement | null,
	active: boolean
): IHeadTurn => {
	const [state, setState] = useState<IHeadTurn>(INITIAL);
	/* Mutated per frame; `setState` only when something the screen shows has
	   actually changed, so the loop does not re-render at 10 Hz. */
	const live = useRef<IHeadTurn>(INITIAL);

	useEffect(() => {
		if (!active || !video) {
			live.current = INITIAL;
			setState(INITIAL);
			return undefined;
		}

		let cancelled = false;
		let frame = 0;
		let lastRun = 0;
		let landmarker: vision.FaceLandmarker | null = null;

		const publish = (next: IHeadTurn) => {
			const prev = live.current;
			live.current = next;
			if (
				prev.status !== next.status ||
				prev.phase !== next.phase ||
				prev.faceVisible !== next.faceVisible ||
				prev.rightDone !== next.rightDone ||
				prev.leftDone !== next.leftDone ||
				prev.passed !== next.passed
			) {
				setState(next);
			}
		};

		const step = (now: number) => {
			if (cancelled) return;
			frame = requestAnimationFrame(step);
			if (!landmarker || now - lastRun < FRAME_INTERVAL_MS) return;
			if (video.readyState < 2 || !video.videoWidth) return;
			lastRun = now;

			let result: vision.FaceLandmarkerResult | undefined;
			try {
				result = landmarker.detectForVideo(video, now);
			} catch {
				/* One bad frame is not a failed check; the next one runs. */
				return;
			}

			const current = live.current;
			const faceVisible = (result?.faceLandmarks?.length ?? 0) > 0;
			const categories = result?.faceBlendshapes?.[0]?.categories ?? [];
			const inRight = scoreOf(categories, 'eyeLookInRight');
			const inLeft = scoreOf(categories, 'eyeLookInLeft');

			let { phase, rightDone, leftDone, passed } = current;
			if (faceVisible && !passed) {
				if (phase === 'right' && inRight > RIGHT_THRESHOLD) {
					rightDone = true;
					phase = 'left';
				} else if (phase === 'left' && inLeft > LEFT_THRESHOLD) {
					leftDone = true;
					phase = 'straight';
				} else if (
					phase === 'straight' &&
					inRight < STRAIGHT_MAX &&
					inLeft < STRAIGHT_MAX
				) {
					phase = 'done';
					passed = true;
				}
			}
			publish({ ...current, status: 'ready', faceVisible, phase, rightDone, leftDone, passed });
		};

		const start = async () => {
			publish({ ...INITIAL, status: 'loading' });
			try {
				const files = await FilesetResolver.forVisionTasks(WASM_URL);
				const create = (delegate: 'GPU' | 'CPU') =>
					FaceLandmarker.createFromOptions(files, {
						baseOptions: { modelAssetPath: MODEL_URL, delegate },
						outputFaceBlendshapes: true,
						runningMode: 'VIDEO',
						numFaces: 1,
					});
				/* GPU first, CPU if the context refuses — the applicant flow's order. */
				try {
					landmarker = await create('GPU');
				} catch {
					landmarker = await create('CPU');
				}
			} catch {
				landmarker = null;
			}
			if (cancelled) {
				landmarker?.close();
				return;
			}
			if (!landmarker) {
				publish({ ...INITIAL, status: 'bypassed', passed: true });
				return;
			}
			publish({ ...INITIAL, status: 'ready' });
			frame = requestAnimationFrame(step);
		};

		void start();

		return () => {
			cancelled = true;
			cancelAnimationFrame(frame);
			landmarker?.close();
			landmarker = null;
		};
	}, [active, video]);

	return state;
};
